// src/components/extensions/ExtensionGrid.styles.ts
// List container for the Extensions page.
//
// ONE COLUMN, ON PURPOSE. This page is a showcase rather than an index, so each
// extension gets the full 1120px measure and reads one at a time rather than as
// a pair to be compared side by side. The projects grid can afford three-up
// because a project card is a summary; an extension card carries a feature
// list, an install command and a link row, and those need the width.
//
// The scroll progress rule above the list is the page's one scroll-linked
// element. It is a plain div with a transform driven from ExtensionGrid, not a
// scroll listener or an effect, so it cannot cost a re-render per frame.
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';

/**
 * Shared reduced-motion guard. The progress fill and the icon entrance both
 * outrank the global block in globalStyles.ts, so the guard is repeated here for
 * the same reason Navbar.styles.ts keeps its own copy.
 */
const noMotion = css`
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/**
 * The list. A motion.ul so the cards can be sequenced by the page's own motion
 * language rather than by N separate whileInView triggers.
 */
export const GridRoot = styled(motion.ul)`
  position: relative;
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
`;

/* ------------------------------------------------------- scroll progress */

/** Hairline track. Sits in the header's bottom margin, above the first card. */
export const ProgressTrack = styled.div`
  position: relative;
  width: 100%;
  height: 2px;
  margin-bottom: 1.75rem;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.borderColor};

  ${noMotion}
`;

/**
 * The fill. `scaleX` rather than `width` so the browser animates it on the
 * compositor and it never triggers layout.
 *
 * `transform-origin: left` plus an inline `scaleX` from the grid. The guard below
 * is what makes reduced motion honest: the fill is simply not drawn, because a
 * progress bar that quietly stops tracking is worse than one that was never
 * there. Left at 1 in that case it would read as "finished" immediately, which
 * would be a lie.
 */
export const ProgressFill = styled(motion.div)`
  position: absolute;
  inset: 0;
  transform-origin: left center;
  background: ${({ theme }) => theme.accent};

  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`;

/* ----------------------------------------------------------------- items */

/**
 * Each extension is a list item, so the page is a real list and the <article>
 * inside carries the extension heading.
 *
 * min-width: 0 lets a long extension name or install command wrap instead of
 * stretching the column.
 */
export const GridItem = styled(motion.li)`
  display: flex;
  min-width: 0;

  /* The gap lives on the item rather than as a grid gap so the last card is not
     followed by a stray half-gap. */
  &:not(:last-child) {
    margin-bottom: 1.5rem;
  }
`;
